
import { Component, Prop, Vue } from 'vue-property-decorator';
import { CompositionDefinition } from '@/config/compositionConfig';
import { firstUsableImageUrl } from '@/helpers/variantImage';
import { Column } from '@/models/report'; // Use exported Column interface

@Component
export default class CompositionCellRenderer extends Vue {
  /**
   * The usable-image picker, exposed so the template can call it. A Vue class
   * component cannot call a module function from a template unless it is a
   * class property — without this the v-if evaluates `undefined` as a
   * function and Vue warns at runtime while the build stays green.
   */
  private firstUsableImageUrl = firstUsableImageUrl;

  @Prop({ required: true }) item!: any;
  @Prop({ required: true, type: String }) compositionKey!: string;
  @Prop({ required: true }) config!: CompositionDefinition | null; // Allow null for safety
  @Prop({ required: true, type: Set }) selectedCodes!: Set<string>;
  @Prop({ required: true, type: Array }) reportColumns!: Column[]; // Use Column[] type
  /**
   * AS-10775 — `code -> custom label` for columns the merchant renamed. Optional
   * so the other call sites of this component keep working unchanged; without it
   * the server metadata name is used exactly as before.
   */
  @Prop({ required: false, default: null }) columnLabels!: Map<string, string> | null;

  // --- COMPUTED --- //

  /**
   * Memoized map for quick lookup of column definitions by code.
   */
  get columnDefsMap(): Map<string, Column> { // Use Column type
    const map = new Map<string, Column>(); // Use Column type
    if (this.reportColumns) {
      this.reportColumns.forEach((col) => map.set(col.code, col));
    }
    return map;
  }

  // --- METHODS --- //

  /**
   * Checks if a constituent should be rendered based on selection and data presence.
   */
  public shouldRender(code: string): boolean {
    const isSelected = this.selectedCodes.has(code); // Check selection state FIRST
    if (!this.config || !isSelected) {
      return false;
    }
    // Check if the value is not null, undefined, or an explicit '-' placeholder
    const value = this.item[code];
    // Also check for empty strings for text types if needed
    // if (this.getColumnDataType(code) === 'text' && value === '') return false;
    return value !== null && value !== undefined && value !== '-';
  }

  /**
   * Retrieves the display label for a given column code.
   */
  public getColumnLabel(code: string): string {
    // AS-10775: a renamed column must read the same inside a composition cell as
    // it does in the header above it.
    const customLabel = this.columnLabels ? this.columnLabels.get(code) : undefined;
    if (customLabel) {
      return customLabel;
    }
    const columnDef = this.columnDefsMap.get(code);
    return columnDef?.name || code; // Fallback to code if label not found
  }

   /**
   * Retrieves the data type for a given column code.
   */
  public getColumnDataType(code: string): string | undefined {
    const columnDef = this.columnDefsMap.get(code);
    return columnDef?.dataType;
  }
}
